// Field Photos availability check — a GeoJSON endpoint (WFS GetFeature in production, a
// committed fixture in dev, see env.ts), run once at map init and again from the Scene Manager's
// Refresh button.
//
// Every OTHER layer's availability (GeoServer WMS rasters/vectors, `datasets`/`workspace_layers`)
// moved server-side at v0.6.3 — GET /api/v1/layers (app/geoserver.py), not a client-side probe
// from here anymore. That move is deliberately NOT a GetCapabilities parse on the server either:
// this file's own earlier version already discovered, in production, that GetCapabilities
// ADVERTISES a layer whose catalog entry exists even when its underlying store can't actually be
// served (a GetMap for it returns a WMS ServiceException, code="LayerNotDefined") — the backend
// now uses the same GetMap-pixel-probe method this file pioneered (see app/geoserver.py's
// module docstring for the full story). Field Photos stays client-side because it isn't a
// GeoServer dataset at all — a different pipeline (Ente-fed), not covered by v0.6.3's registry.
import { FIELD_PHOTOS_URL } from '../config/env';

export type LayerHealth = 'checking' | 'available' | 'missing' | 'unreachable';

/** A missing WFS feature type comes back as HTTP 400 with an ows:ExceptionReport; a missing dev
 * fixture as 404. */
export async function probeFeatureSource(url: string = FIELD_PHOTOS_URL): Promise<LayerHealth> {
  // Don't pull the whole dataset just to see whether it exists.
  const probeUrl = /request=GetFeature/i.test(url) ? `${url}&count=1` : url;
  let res: Response;
  try {
    res = await fetch(probeUrl, { cache: 'no-store', headers: { Accept: 'application/json' } });
  } catch {
    return 'unreachable';
  }
  if (res.status === 200) {
    try {
      const body: unknown = await res.json();
      if (body && typeof body === 'object' && 'type' in body) return 'available';
    } catch {
      // fall through
    }
    return 'unreachable';
  }
  if (res.status === 400 || res.status === 404) return 'missing';
  return 'unreachable';
}
